<template>
  <div v-if="category">
    <div class="thumbImage">
      <img
        class="thumbSp hiddenPc"
        :src="category.spThumbUrl"
        :alt="category.spThumbAlt"
      />
      <img
        class="thumbPc hiddenSp"
        :src="category.pcThumbUrl"
        :alt="category.pcThumbAlt"
      />
    </div>
    <div class="u-containar series">
      <div>
        <HeadingLv3 :text="category.name" />
      </div>
      <p>{{ category.description }}</p>
    </div>
  </div>

  <div class="u-containar responsive">
    <div>
      <HeadingLv3 text="記事一覧" />
      <MagazineCardList :items="outlines" border-bottom articleIndex />
      <Pagination class="pagination" :totalPages="totalPages" />
    </div>
    <MagazineFooter :categories="parentCategories" />
  </div>

  <MagazineEventArchive />
</template>

<script lang="ts">
import { computed, watch, defineComponent, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import Pagination from '@/components/block/Pagination.vue'
import MagazineCardList from '@/components/block/MagazineCardList.vue'
import MagazineFooter from '@/components/block/MagazineFooter.vue'
import MagazineEventArchive from '@/components/block/MagazineEventArchive.vue'
import HeadingLv3 from '@/components/elements/HeadingLv3.vue'
import { useSpecial } from '@/provider/special'
import { MagazineSpecialOutline } from '@/types/MagazineSpecial'

export default defineComponent({
  name: 'MagazinesCategory',

  components: {
    Pagination,
    MagazineCardList,
    MagazineFooter,
    MagazineEventArchive,
    HeadingLv3
  },

  setup() {
    const { state, loadCategory, loadContents } = useSpecial()
    const route = useRoute()
    const parentCategorySlug = computed(() => route.path.split('/')[2])
    const parentCategoryName = computed(
      () =>
        state.categories.find((cat) => cat.slug === parentCategorySlug.value)
          ?.name ?? ''
    )
    const parentCategories = computed(() => {
      if (parentCategorySlug.value === 'series') return state.seriesCategories
      if (parentCategorySlug.value === 'special') return state.specialCategories
      return []
    })

    const loadCategoryContents = () => {
      loadContents({
        perPage: 10,
        category: route.params.id ? Number(route.params.id) : undefined,
        page: route.query.page ? Number(route.query.page) : undefined
      })
    }
    watch(
      () => [route.params.id, route.query.page],
      (next, prev) => {
        if (JSON.stringify(next) === JSON.stringify(prev)) return
        loadCategory()
        loadCategoryContents()
      },
      { immediate: true }
    )

    const totalPages = computed(() => state.totalPages)
    const outlines = computed((): MagazineSpecialOutline[] =>
      state.contents.map((content) => ({
        ...content,
        category: content.childCategories[0]?.name ?? ''
      }))
    )

    const category = computed(() =>
      state.categories.find(
        (category) => category.id === Number(route.params.id)
      )
    )
    const title = computed(() => category?.value?.name ?? '')

    watch(
      () => [title.value, parentCategoryName.value, parentCategorySlug.value],
      (next) => {
        nextTick(() => {
          document.title = `${next[0]} 一覧 | ${next[1]} | 未来のドクターへ、期待以上の出会いを。【マイナビRESIDENT】`
          const lastBread = document.querySelector('.breadCrumb_list li span')
          if (lastBread) {
            lastBread.innerHTML = next[0] ?? ''
          }
          const categoryLinkBread = document.querySelector(
            '.breadCrumb_list li:nth-child(2) a'
          )
          if (categoryLinkBread) {
            categoryLinkBread.innerHTML = `${next[1]} 一覧`
            categoryLinkBread.setAttribute('href', `/magazines/${next[2]}/`)
          }
        })
      },
      { immediate: true }
    )

    return {
      parentCategories,
      totalPages,
      category,
      outlines
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;
.thumbImage {
  width: 100%;

  .thumbPc {
    object-fit: cover;
    width: 100%;
  }
  .thumbSp {
    object-fit: cover;
    width: 100%;
  }
}
.series {
  display: grid;
  grid-template:
    '..' 20px
    'title'
    '..' 20px
    'desc';
  @include mixins.media() {
    grid-template:
      '..' 40px
      'title'
      '..' 20px
      'desc';
  }
  div {
    grid-area: title;
    font-weight: 300;
    font-size: 2rem;
  }
  p {
    grid-area: desc;
    font-size: 1.6rem;
    letter-spacing: 0.05rem;
    white-space: pre-line;
  }
}
.responsive {
  margin-top: 50px;
  @include mixins.media() {
    display: grid;
    grid-template-columns: 1fr 336px;
    gap: 30px;
  }
}
::v-deep .magazineCard_heading {
  font-size: 1.5rem;
}
.pagination {
  width: 100%;
  max-width: 400px;
  margin: 30px auto;
}
.button {
  margin-top: 30px;
}
</style>
